import CustomTooltip from '@gui/components/generic/CustomTooltip';
import { ControllerManager } from '@gui/controller/ControllerManager';
import { useDesignSchemesBucket } from '@gui/store/persistent/persistentStore';
import { Box, Checkbox, Slider, Typography } from '@mui/material';
import type { BoxDesignState, DeepPartial } from '@visualizer/types';
import { DesignSchemeController } from '../DesignSchemeView';

export interface BoxGuiProps {
    setId: string;
    footprintId: string;
    schemeId: string;
}

export const BoxView: React.FC<BoxGuiProps> = ({ setId, footprintId, schemeId }) => {
    const designSchemes = useDesignSchemesBucket((s) => s);
    const scheme = designSchemes.sets?.[setId]?.footprints?.[footprintId]?.designSchemes?.[schemeId];
    const boxState = scheme.box;
    const defaults = designSchemes.defaults.box;

    const active = boxState?.active ?? defaults.active;
    const transparent = boxState?.transparent ?? defaults.transparent;
    const snapToVolume = boxState?.snapToVolume ?? defaults.snapToVolume;
    const alpha = boxState?.alpha ?? defaults.alpha;

    const updateBox = (partial: DeepPartial<BoxDesignState>) => {
        const controller = ControllerManager.getInstance().getDesignSchemeController();
        controller.updateDesignScheme(setId, footprintId, schemeId, {
            box: partial,
        });
    };

    const formatNoDecimals = (value: number) => `${Math.round(value)}`;

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                <CustomTooltip
                    mode="icon"
                    side="right"
                    title={
                        <Typography
                            sx={{
                                fontSize: '10.5px',
                                lineHeight: 1.4,
                                color: 'text.secondary',
                            }}
                        >
                            Draws a bounding box around the active sensor points with a configurable horizontal rotation angle.
                        </Typography>
                    }
                >
                    <Typography variant="subtitle1" align="center" sx={{ textAlign: 'center' }}>
                        Box
                    </Typography>
                </CustomTooltip>
            </Box>

            <Box mt={1}>
                <DesignSchemeController schemeId={schemeId} footprintId={footprintId} setId={setId} />
            </Box>

            {active && (
                <>
                    <Box mt={1} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Checkbox
                            data-testid="ds-box-transparent-checkbox"
                            size="small"
                            checked={transparent}
                            onChange={(e) => updateBox({ transparent: e.target.checked })}
                            sx={{ padding: 0, '& .MuiSvgIcon-root': { fontSize: '14px' } }}
                        />
                        <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Transparent</Typography>

                        <Checkbox
                            data-testid="ds-box-snap-to-volume-checkbox"
                            size="small"
                            checked={snapToVolume}
                            onChange={(e) => updateBox({ snapToVolume: e.target.checked })}
                            sx={{ padding: 0, '& .MuiSvgIcon-root': { fontSize: '14px' } }}
                        />
                        <Typography sx={{ fontSize: '10px', whiteSpace: 'nowrap' }}>Snap to volume</Typography>
                    </Box>

                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                            width: '100%',
                            minWidth: 0,
                            mt: 1,
                        }}
                    >
                        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, whiteSpace: 'nowrap' }}>
                            <Typography variant="body2">Angle</Typography>
                            <CustomTooltip
                                mode="icon"
                                side="right"
                                title="Rotation around the vertical axis, relative to the grid angle. 0° means no additional rotation (box aligned with the grid)"
                            />
                        </Box>

                        <Slider
                            data-testid="ds-box-alpha-slider"
                            className="panel-slider"
                            size="small"
                            value={alpha.value}
                            min={alpha.minValue}
                            max={alpha.maxValue}
                            step={1}
                            disabled={snapToVolume}
                            valueLabelDisplay="on"
                            valueLabelFormat={formatNoDecimals}
                            onChange={(_, value) => {
                                const nextValue = Array.isArray(value) ? value[0] : value;
                                updateBox({
                                    alpha: { ...alpha, value: nextValue },
                                });
                            }}
                            sx={{ flex: '1 1 0', minWidth: 0 }}
                        />
                        <Typography variant="body2" whiteSpace="nowrap" sx={{ ml: 0.5, textAlign: 'right', flex: '0 0 auto' }}>
                            [°]
                        </Typography>
                    </Box>
                </>
            )}
        </Box>
    );
};
